import { setupLayouts } from 'virtual:generated-layouts';
import { createRouter, createWebHistory } from 'vue-router';

import { AGENCY_ROLES, ROLE_TYPES } from '@/constants/roles';
import { useAuthStore } from '@/stores/agency/auth';
import { useAgencyStore } from '@/stores/agency/settings/agency/profile';
import { useUserManagementStore } from '@/stores/agency/settings/user-management/user-management';

import { isOwnerLoggedIn } from './guards/owner.guard';
import {
  getPermissionKeyForRoute,
  hasRequiredPermission,
  isPowerRole,
  isPowerRoleOnlyRoute
} from './guards/permission.guard';
import { isUserLoggedIn } from './utils';

import routes from '~pages';

const router = createRouter({
  history: createWebHistory(),
  routes: [...setupLayouts(routes)]
});

router.beforeEach((to, from, next) => {
  const isLoggedIn = isUserLoggedIn();
  const agencyStore = useAgencyStore();
  const permissionStore = useUserManagementStore();
  const authStore = useAuthStore();
  const agencyProfile = computed(() => agencyStore.getAgencyProfile);

  // Owner auth guard — protect /owner/* routes (except /owner/login and /owner/impersonate)
  if (to.matched.some(record => record.meta.requiresOwnerAuth)) {
    if (!isOwnerLoggedIn()) {
      return next('/owner/login');
    }
  }

  // Redirect already logged-in owner away from owner login
  if (to.path === '/owner/login' && isOwnerLoggedIn()) {
    return next('/owner');
  }

  // Signup is not an accessible flow — block direct navigation to it from anywhere
  if (to.name === 'signup' || to.path === '/signup') {
    return next({ name: 'index' });
  }

  // Redirect already logged-in users away from the login page
  if ((to.name === 'index' || to.path === '/') && isLoggedIn) {
    const userRole = localStorage.getItem('userRole');
    const hasParentTenant = !!localStorage.getItem('parentTenant');

    if (userRole === ROLE_TYPES.POINT_OF_CONTACT) {
      return next('/contact/dashboard');
    }

    if (hasParentTenant) {
      return next('/branch/home');
    }

    if (userRole === ROLE_TYPES.SUPERADMIN || userRole === ROLE_TYPES.ADMIN) {
      return next('/agency/home');
    }

    if (AGENCY_ROLES.includes(userRole as any)) {
      return next('/agency/home');
    }

    return next('/agency/home');
  }

  // Redirect to login if authentication is required
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!isLoggedIn) {
      return next({ name: 'index' });
    }
  }

  // Owner-viewer impersonation sessions bypass all permission checks
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  if (userData?.isOwnerViewer) {
    return next();
  }

  // Check permissions for non-power roles
  const roleSlug = authStore.loggedInUserProfile?.role?.slug;
  const hasPowerRole = isPowerRole(roleSlug);

  if (!hasPowerRole) {
    // Settings and Activity Log are only accessible to power roles (super admin and admin)
    if (isPowerRoleOnlyRoute(to)) {
      return next({ name: 'not-authorized' });
    }

    const permissionToCheck = getPermissionKeyForRoute(to);

    if (!hasRequiredPermission(permissionToCheck, key => permissionStore.userHasPermission(key))) {
      return next({ name: 'not-authorized' });
    }
  }

  const DEFAULT_FAVICON = '/favicon.png';

  // Apply favicon from localStorage immediately to avoid flicker before API loads
  const cachedProfile = localStorage.getItem('agencyProfile');

  if (cachedProfile) {
    const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;

    link.href = JSON.parse(cachedProfile)?.favicon || DEFAULT_FAVICON;
  }

  watch(
    () => agencyProfile.value,
    () => {
      const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;

      link.href = agencyProfile.value.favicon || DEFAULT_FAVICON;
    },
    { immediate: true }
  );

  next();
});

export default router;
